/**
 * @file goldPayPendingOAuth.js
 * @description JSAPI 橘子币充值 OAuth 跳转前暂存上下文
 * @module features/user/utils/goldPayPendingOAuth
 */

const PENDING_GOLD_STORAGE_KEY = 'comic:pendingGoldPay'
const PENDING_GOLD_RESUME_DONE_KEY = 'comic:goldPayResumeDoneId'

/**
 * @typedef {Object} PendingGoldPayResumeContext
 * @property {'vol-pay'|'user-center'} source
 * @property {string} packageCode
 * @property {string} [comicId]
 * @property {string} [volId]
 * @property {string} [bulkType]
 * @property {string} [idempotencyKey]
 * @property {string} [resumeSessionId]
 * @property {string} [expectedUserId]
 * @property {string} [expectedUserPhone]
 * @property {Record<string, unknown>} [preOAuthSession]
 */

/**
 * @returns {string}
 */
function createResumeSessionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `gold-resume-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * @param {PendingGoldPayResumeContext} context
 */
export function savePendingGoldPayResume(context) {
  if (typeof sessionStorage === 'undefined') return
  const resumeSessionId =
    typeof context.resumeSessionId === 'string' && context.resumeSessionId.trim()
      ? context.resumeSessionId.trim()
      : createResumeSessionId()
  sessionStorage.setItem(
    PENDING_GOLD_STORAGE_KEY,
    JSON.stringify({ ...context, resumeSessionId })
  )
}

export function clearPendingGoldPayResume() {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(PENDING_GOLD_STORAGE_KEY)
  sessionStorage.removeItem(PENDING_GOLD_RESUME_DONE_KEY)
}

/**
 * @param {string} sessionId
 * @returns {boolean}
 */
export function isGoldPayResumeSessionDone(sessionId) {
  if (typeof sessionStorage === 'undefined' || !sessionId?.trim()) return false
  return sessionStorage.getItem(PENDING_GOLD_RESUME_DONE_KEY) === sessionId.trim()
}

/**
 * @param {string} sessionId
 */
export function markGoldPayResumeSessionDone(sessionId) {
  if (typeof sessionStorage === 'undefined' || !sessionId?.trim()) return
  sessionStorage.setItem(PENDING_GOLD_RESUME_DONE_KEY, sessionId.trim())
}

/**
 * @returns {PendingGoldPayResumeContext|null}
 */
export function peekPendingGoldPayResume() {
  if (typeof sessionStorage === 'undefined') return null
  const raw = sessionStorage.getItem(PENDING_GOLD_STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !parsed.packageCode) return null
    return parsed
  } catch {
    return null
  }
}

/**
 * @returns {PendingGoldPayResumeContext|null}
 */
export function consumePendingGoldPayResume() {
  if (typeof sessionStorage === 'undefined') return null
  const raw = sessionStorage.getItem(PENDING_GOLD_STORAGE_KEY)
  if (!raw) return null
  sessionStorage.removeItem(PENDING_GOLD_STORAGE_KEY)
  try {
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !parsed.packageCode) return null
    return parsed
  } catch {
    return null
  }
}
